import React from 'react';
import clsx from 'clsx';
import { Field } from 'redux-form';
import { makeStyles } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';

import { CustomIcon } from '../index';
import { DEFAULT_DATE_FORMAT } from '../../../utils/time';

const useStyles = makeStyles(theme => ({
  inputRoot: {
    ...theme.typography.body1,
    borderRadius: theme.shape.borderRadius * 8,
    color: theme.palette.text.primary,

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.primary
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.primary
    }
  },
  inputHint: {
    color: theme.palette.text.hint
  },
  inputRootError: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.warning.dark
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.warning.dark
    }
  },
  inputRootFilled: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.primary
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main
    }
  },
  input: {
    padding: `${theme.spacing(0.5)}px 0 ${theme.spacing(0.5)}px ${theme.spacing(
      2
    )}px`,
    width: 82,
    height: 'auto'
  },
  adornment: {
    marginLeft: 0
  }
}));

const FormDateSelectComponent = React.forwardRef(
  (
    {
      className,
      input,
      disabled,
      id,
      label,
      error,
      minDate,
      maxDate,
      shouldDisableDateCheck,
      hintMode
    },
    ref
  ) => {
    const classes = useStyles();

    const handleChange = date => {
      if (input.onChange) {
        if (date) {
          if (minDate && maxDate) {
            if (date.isSameOrAfter(minDate) && date.isSameOrBefore(maxDate)) {
              input.onChange(date.valueOf());
            } else {
              input.onChange(null);
            }
          } else {
            input.onChange(date.valueOf());
          }
        } else {
          input.onChange(null);
        }
      }
    };

    const handleOpen = () => {
      input &&
        input.onFocus &&
        input.onFocus({
          target: { name: input.name, value: input.value },
          persist: () => {}
        });
    };

    const handleClose = date => {
      if (date) {
        input && input.onBlur && input.onBlur(date.valueOf());
      }
    };

    return (
      <KeyboardDatePicker
        disableToolbar
        autoOk
        margin="none"
        variant="inline"
        inputVariant="outlined"
        disabled={disabled}
        name={input.name}
        format={DEFAULT_DATE_FORMAT}
        id={id}
        label={label}
        value={input.value}
        onChange={handleChange}
        onOpen={handleOpen}
        onClose={handleClose}
        onAccept={handleClose}
        shouldDisableDate={shouldDisableDateCheck}
        minDate={minDate}
        maxDate={maxDate}
        InputProps={{
          error: error,
          classes: {
            root: clsx(
              classes.inputRoot,
              hintMode && classes.inputHint,
              error && classes.inputRootError,
              !error && input.value != null && classes.inputRootFilled,
              className
            ),
            input: classes.input
          }
        }}
        InputAdornmentProps={{
          classes: {
            root: classes.adornment
          }
        }}
        keyboardIcon={
          <CustomIcon icon="calendar" size="xs" variant="primary" />
        }
        KeyboardButtonProps={{
          id: `${id}-button`,
          'aria-label': 'change date',
          size: 'small'
        }}
        invalidDateMessage={null}
        minDateMessage={null}
        maxDateMessage={null}
      />
    );
  }
);

const FormDateSelect = React.forwardRef(
  ({ onChange, onFormat, name, validate, ...props }, ref) =>
    name ? (
      <Field
        component={FormDateSelectComponent}
        name={name}
        validate={validate}
        onChange={onChange}
        format={onFormat}
        props={{ ...props, ref }}
      />
    ) : (
      <FormDateSelectComponent {...props} {...{ ref, onChange }} />
    )
);

export default FormDateSelect;
